import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { IPayment } from "@ontime/types";
import PaymentService from '../../services/PaymentService';
import InfoComponent from '../../customComponents/InfoComponent';
import PaymentIcon from '@mui/icons-material/Payment';
import AppointmentPaymentForm from './AppointmentPaymentForm';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { PaymentMethod, PaymentStatus } from "@ontime/types";

interface AppointmentPaymentListFormProps {
    appointmentId: string;
    clientId?: string;
    organizationId: string;
    appointmentAmount?: number;
    disabled: boolean;
    dontShow: boolean;
}

const AppointmentPaymentList: React.FC<AppointmentPaymentListFormProps> = ({ appointmentId, organizationId, disabled, dontShow, appointmentAmount, clientId }) => {

    const [loading, setLoading] = useState(false);

    const [payments, setPayments] = useState<IPayment[]>([]);

    const paymentType: GridColumnType<IPayment> = {
        paymentId: { type: "Id", dontShow: true },
        organizationId: { type: "Id", dontShow: true },
        appointmentId: { type: "Id", dontShow: true },
        clientId: { type: "Id", dontShow: true },
        amount: { type: "Price", header: 'Amount' },
        finalAmount: { type: "Price", header: 'Final Amount' },
        status: { type: "Enum", header: 'Status', enumObject: PaymentStatus, dontShow: true },
        method: { type: "Enum", header: 'Method', enumObject: PaymentMethod, dontShow: true },
        earnedPoints: { type: "Integer", header: 'Earned Points' },
    };

    useEffect(() => {

        if (appointmentId && organizationId) {
            getAllPayment()
        }
    }, [appointmentId]);

    const getAllPayment = async () => {
        setLoading(true);
        setPayments([]);
        try {
            const response = await PaymentService.GetList(organizationId, appointmentId);
            console.log('getAllPayment', response)
            setPayments(response);
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const [paymentForm, setPaymentForm] = useState<{ open: boolean; payment?: IPayment; appointmentAmount?: number; }>({ open: false, });

    const openPayment = (input?: IPayment) => {
        console.log('openPayment', input)
        const prierAmountPaymentSum = (payments.map((item) => item.amount).reduce((sum, value) => sum + value, 0));

        setPaymentForm({ open: true, payment: input, appointmentAmount: (appointmentAmount ?? 0) - prierAmountPaymentSum });
    };

    const closePayment = (input?: IPayment) => {

        setPaymentForm({ open: false });
        if (input?.paymentId) {
            if (payments.find((item) => item.paymentId === input.paymentId)) {
                setPayments([input, ...payments.filter((item) => item.paymentId !== input.paymentId)]);
            }
            else {
                setPayments([input, ...payments]);
            }
        }
    };

    const deletePayment = async (input?: IPayment) => {
        setLoading(true);

        try {
            await PaymentService.Delete(organizationId, appointmentId, input?.paymentId ?? '', input?.isDeleted ?? false);

            setPayments([...payments.filter((item) => item.paymentId !== (input?.paymentId ?? 0))]);
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        dontShow ?
            <>
                {!(payments.length > 0) && !paymentForm.open &&
                    <CustomDialogContent >
                        <Box style={{ width: '180px' }}>
                            <InfoComponent infoContent='Make payment'>
                                <Button
                                    variant="contained"
                                    size='small'
                                    onClick={() => openPayment()}
                                    disabled={loading || disabled}
                                    fullWidth
                                ><PaymentIcon /> Make payment</Button>
                            </InfoComponent>
                        </Box>
                    </CustomDialogContent>
                }
                {((payments.length > 0) && !paymentForm.open) &&
                    <GridTable<IPayment, IPayment, IPayment>
                        gridTitle='Payments'
                        loading={loading}
                        list={payments.map((item) => ({ ...item, id: item.paymentId }))}
                        inputType={paymentType}
                        onClickNew={openPayment}
                        onEditRow={openPayment}
                        onClickRow={openPayment}
                        onDeleteRow={deletePayment}
                        isOnDialog
                    />
                }
                {paymentForm.open &&
                    <AppointmentPaymentForm
                        closeForm={closePayment}
                        paymentInput={paymentForm.payment}
                        appointmentId={appointmentId}
                        organizationId={organizationId}
                        appointmentAmount={(paymentForm.appointmentAmount ?? 0) > 0 ? paymentForm.appointmentAmount : 0}
                        clientId={clientId}
                    />
                }
            </>
            : null
    );
};

export default AppointmentPaymentList;